import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Loader2, Sparkles, FileText, AlertCircle, Info } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  generateReviewPeriodString,
  getReviewPeriodLabel,
  type ReviewCadence,
} from "@/lib/reviewPeriodUtils";

interface Customer {
  id: number;
  name: string;
  reviewFrequency?: string;
}

interface Assessment {
  id: number;
  title: string;
  status: string;
  customerId: number;
}

interface Qbr {
  id: number;
  customerId: number;
  reviewPeriod?: string;
  quarter?: string;
}

function getPastReviewPeriodOptions(cadence: ReviewCadence, count: number = 8): string[] {
  const now = new Date();
  const periods: string[] = [];
  
  // Calculate months to step back based on cadence
  const monthsPerPeriod: Record<ReviewCadence, number> = {
    monthly: 1,
    quarterly: 3,
    biannual: 6,
    annual: 12,
  };
  
  const step = monthsPerPeriod[cadence];
  
  for (let i = 1; i <= count; i++) {
    // Step back by i periods from current date
    const monthsBack = i * step;
    const date = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
    periods.push(generateReviewPeriodString(date, cadence));
  }

  return [...new Set(periods)];
}

interface CreateBusinessReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preselectedCustomerId?: number;
}

export function CreateBusinessReviewDialog({
  open,
  onOpenChange,
  preselectedCustomerId,
}: CreateBusinessReviewDialogProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<string>("");
  const [selectedPeriod, setSelectedPeriod] = useState<string>("");
  const [generationMode, setGenerationMode] = useState<"ai" | "traditional">("ai");

  // Fetch customers
  const { data: customers = [] } = useQuery<Customer[]>({
    queryKey: ["/api/customers"],
  });

  // Fetch assessments
  const { data: assessments = [] } = useQuery<Assessment[]>({
    queryKey: ["/api/assessments"],
  });

  // Fetch existing QBRs to check for duplicates
  const { data: existingQbrs = [] } = useQuery<Qbr[]>({
    queryKey: ["/api/qbrs"],
  });

  // Set preselected customer
  useEffect(() => {
    if (preselectedCustomerId && open) {
      setSelectedCustomerId(preselectedCustomerId.toString());
    }
  }, [preselectedCustomerId, open]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedCustomerId(preselectedCustomerId?.toString() || "");
      setSelectedAssessmentId("");
      setSelectedPeriod("");
      setGenerationMode("ai");
    }
  }, [open, preselectedCustomerId]);

  // Get all approved/completed assessments
  const eligibleAssessments = assessments.filter(
    (a: any) => a.status === "approved" || a.status === "completed"
  );

  // Filter customers to only those with at least one eligible assessment
  const customersWithAssessments = customers.filter((c: any) =>
    eligibleAssessments.some((a: any) => a.customerId?.toString() === c.id.toString())
  );

  // Get selected customer details
  const selectedCustomer = customers.find(
    (c: any) => c.id.toString() === selectedCustomerId
  );

  // Filter assessments for the selected customer (only approved or completed ones)
  const customerAssessments = eligibleAssessments.filter(
    (a: any) => a.customerId?.toString() === selectedCustomerId
  );

  // Get review cadence from selected customer
  const reviewCadence = (selectedCustomer?.reviewFrequency || "quarterly") as ReviewCadence;
  
  // Get period options based on customer's cadence (past periods only)
  const periodOptions = getPastReviewPeriodOptions(reviewCadence, 8);
  const periodLabel = getReviewPeriodLabel(reviewCadence);

  // Auto-select the most recent previous period when customer/assessment change
  useEffect(() => {
    if (selectedCustomerId && selectedAssessmentId && periodOptions.length > 0) {
      setSelectedPeriod(periodOptions[0]);
    }
  }, [selectedCustomerId, selectedAssessmentId, reviewCadence]);

  // Check if a QBR already exists for this customer and period
  const existingReviewForPeriod = existingQbrs.find(
    (q: any) =>
      q.customerId?.toString() === selectedCustomerId &&
      (q.reviewPeriod === selectedPeriod || q.quarter === selectedPeriod)
  );

  // Create QBR mutation
  const createQbrMutation = useMutation({
    mutationFn: async () => {
      const selectedAssessment = customerAssessments.find(
        (a: any) => a.id.toString() === selectedAssessmentId
      );

      if (!selectedAssessment || !selectedCustomer) {
        throw new Error("Please select a customer and assessment");
      }

      // Create QBR data - backend will handle AI generation based on generationMode
      const qbrData: any = {
        title: `${selectedCustomer.name} - ${selectedPeriod} Business Review`,
        assessmentId: parseInt(selectedAssessmentId),
        customerId: parseInt(selectedCustomerId),
        assignedToId: (user as any)?.id?.toString() || "",
        quarter: selectedPeriod,
        reviewPeriod: selectedPeriod,
        status: "draft",
        executiveSummary: "",
        keyFindings: [],
        recommendations: [],
        generationMode: generationMode, // Backend uses this to determine AI vs traditional generation
      };

      // Create the QBR - backend handles AI/traditional generation based on generationMode
      const response = await apiRequest("POST", "/api/qbrs", qbrData);
      return response;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/qbrs"] });
      queryClient.invalidateQueries({ queryKey: ["/api/business-reviews/upcoming"] });
      toast({
        title: "Business Review Created",
        description: `Business Review for ${selectedCustomer?.name} has been created.`,
      });
      onOpenChange(false);
      
      // Navigate to the new QBR
      if (data?.id) {
        setLocation(`/qbrs/${data.id}`);
      }
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Create Business Review",
        description: error.message || "An error occurred while creating the Business Review.",
        variant: "destructive",
      });
    },
  });

  const handleCreate = () => {
    if (!selectedCustomerId) {
      toast({
        title: "Customer Required",
        description: "Please select a customer for this Business Review.",
        variant: "destructive",
      });
      return;
    }

    if (!selectedAssessmentId) {
      toast({
        title: "Assessment Required",
        description: "Please select an assessment to base this Business Review on.",
        variant: "destructive",
      });
      return;
    }

    if (!selectedPeriod) {
      toast({
        title: "Period Required",
        description: `Please select a ${periodLabel.toLowerCase()} for this Business Review.`,
        variant: "destructive",
      });
      return;
    }

    createQbrMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Business Review</DialogTitle>
          <DialogDescription>
            Manually create a new Business Review by selecting a customer,
            assessment, and review period.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Customer Selection */}
          <div className="space-y-2">
            <Label htmlFor="customer">Customer</Label>
            <Select
              value={selectedCustomerId}
              onValueChange={(value) => {
                setSelectedCustomerId(value);
                setSelectedAssessmentId("");
                setSelectedPeriod("");
              }}
              disabled={!!preselectedCustomerId}
            >
              <SelectTrigger id="customer">
                <SelectValue placeholder="Select a customer" />
              </SelectTrigger>
              <SelectContent>
                {customersWithAssessments.map((customer: any) => (
                  <SelectItem key={customer.id} value={customer.id.toString()}>
                    {customer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Info className="h-4 w-4 flex-shrink-0" />
              <span>Only customers with at least one approved assessment are shown.</span>
            </div>
          </div>

          {/* Assessment Selection */}
          {selectedCustomerId && (
            <div className="space-y-2">
              <Label htmlFor="assessment">Assessment</Label>
              {customerAssessments.length === 0 ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 bg-muted rounded-md">
                  <AlertCircle className="h-4 w-4" />
                  No approved assessments available for this customer.
                </div>
              ) : (
                <Select
                  value={selectedAssessmentId}
                  onValueChange={setSelectedAssessmentId}
                >
                  <SelectTrigger id="assessment">
                    <SelectValue placeholder="Select an assessment" />
                  </SelectTrigger>
                  <SelectContent>
                    {customerAssessments.map((assessment: any) => (
                      <SelectItem
                        key={assessment.id}
                        value={assessment.id.toString()}
                      >
                        <div className="flex items-center gap-2">
                          <span>{assessment.title}</span>
                          <Badge variant="outline" className="text-xs">
                            {assessment.status}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* Period Selection */}
          {selectedCustomerId && selectedAssessmentId && (
            <div className="space-y-2">
              <Label htmlFor="period">{periodLabel}</Label>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger id="period">
                  <SelectValue placeholder={`Select ${periodLabel.toLowerCase()}`} />
                </SelectTrigger>
                <SelectContent>
                  {periodOptions.map((period: string) => (
                    <SelectItem key={period} value={period}>
                      {period}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {existingReviewForPeriod && (
                <div className="flex items-center gap-2 text-sm text-amber-600 p-2 bg-amber-50 rounded-md">
                  <AlertCircle className="h-4 w-4" />
                  A Business Review already exists for this period.
                </div>
              )}
            </div>
          )}

          {/* Generation Mode Selection */}
          {selectedCustomerId && selectedAssessmentId && selectedPeriod && (
            <div className="space-y-3">
              <Label>Content Generation</Label>
              <RadioGroup
                value={generationMode}
                onValueChange={(value) => setGenerationMode(value as "ai" | "traditional")}
                className="space-y-3"
              >
                <label 
                  htmlFor="ai"
                  className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-accent/50 cursor-pointer"
                >
                  <RadioGroupItem value="ai" id="ai" className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 font-medium">
                      <Sparkles className="h-4 w-4 text-purple-500" />
                      AI-Enhanced Content
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Automatically generate executive summary, findings, and recommendations
                      based on the assessment data.
                    </p>
                  </div>
                </label>
                
                <label
                  htmlFor="traditional"
                  className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-accent/50 cursor-pointer"
                >
                  <RadioGroupItem value="traditional" id="traditional" className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 font-medium">
                      <FileText className="h-4 w-4 text-blue-500" />
                      Manual Entry
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Start with a blank template and manually enter all content.
                    </p>
                  </div>
                </label>
              </RadioGroup>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createQbrMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={
              !selectedCustomerId ||
              !selectedAssessmentId ||
              !selectedPeriod ||
              !!existingReviewForPeriod ||
              createQbrMutation.isPending
            }
          >
            {createQbrMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {generationMode === "ai" ? "Generating..." : "Creating..."}
              </>
            ) : (
              "Create Business Review"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
